import React, { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { MenuProps } from 'antd';
import { useAtom } from 'jotai/index';
import { useTranslation } from 'react-i18next';
import { Table } from 'common/components/antd/Table/Table';
import { useCustomerAccountsListQuery } from 'domains/accounts/queries/accounts.query';
import { accountsAtom } from 'domains/accounts/store/accounts-store';
import { SDangerActionItem } from '../customers.page.styles';
import { EAccountManagementCustomersAction, EAccountManagementCustomersInnerColumnKeys } from '../enums/customers.enum';
import { getInnerColumns } from '../table/table.columns';
import {
  TItemClickEventHandler,
  TCustomerAccountsInnerRecord,
  TCustomersAccountRecord,
} from '../types/customers.types';

export const ExpandedRowContentComponent = ({
  record,
  counterparty,
}: {
  record: TCustomersAccountRecord;
  counterparty: string;
}): ReactElement => {
  const { t } = useTranslation();

  const [state, setState] = useAtom(accountsAtom);

  const actions: MenuProps['items'] = useMemo(
    () => [
      {
        key: EAccountManagementCustomersAction.Edit,
        label: t('accountManagement.customers.table.menu.action.edit'),
      },
      {
        key: EAccountManagementCustomersAction.Block,
        label: t('accountManagement.customers.table.menu.action.block'),
      },
      {
        key: EAccountManagementCustomersAction.Delete,
        label: <SDangerActionItem>{t('accountManagement.customers.table.menu.action.delete')}</SDangerActionItem>,
      },
    ],
    [],
  );

  const onItemActionClick = useCallback<TItemClickEventHandler>(({ item }) => {
    const fn = ({ key }: { key: string }): void => {
      switch (key) {
        case EAccountManagementCustomersAction.Edit: {
          // TODO: need to find another solution.
          console.warn(item);
          // navigate(
          //   ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_EDIT_CUSTOMER_ACCOUNT.replace(
          //     ERoutePathParams.Account,
          //     item?.guid,
          //   ),
          // );
          break;
        }
        case EAccountManagementCustomersAction.Block: {
          throw new Error('Block is not implemented');
          break;
        }
        case EAccountManagementCustomersAction.Delete: {
          throw new Error('Deletion is not implemented');
          break;
        }
        default: {
          throw new Error(`click handler for menu item with key [${key}] is not implemented`);
        }
      }
    };
    return fn;
  }, []);

  const innerColumns = getInnerColumns({
    menuItems: actions,
    onItemActionClick,
  });

  const { data: accountsCustomerData, isLoading: isAccountsLoading } = useCustomerAccountsListQuery(
    counterparty,
    record?.wallet_user_guid,
    {},
    {
      refetchOnMount: false,
    },
  );

  useEffect(() => {
    if (state?.updateTableStatus) {
      setTimeout(() => {
        setState({
          ...state,
          updateTableStatus: false,
          accounts: [],
          accountNumber: '',
        });
      }, 3000);
    }
  }, [state?.updateTableStatus]);

  const dataAccounts: TCustomerAccountsInnerRecord[] = (accountsCustomerData?.accounts || []).map((item, index) => ({
    ...item,
    row_number: index + 1,
    account_number: item?.guid,
    currency: item?.assets[0]?.code,
    account_name: item?.name,
    account_status: item?.blocked ? 2 : 1,
  }));

  return (
    <Table
      columns={innerColumns}
      dataSource={dataAccounts}
      loading={isAccountsLoading}
      pagination={false}
      rowKey={EAccountManagementCustomersInnerColumnKeys.AccountNumber}
      onRow={(row): { style: {background: string | undefined } } => ({
        style: {
          background: row.account_number === state?.accountNumber ? '#E6FFCD' : undefined,
        },
      })}
    />
  );
};
